import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  User, 
  Eye, 
  Heart, 
  ArrowLeft, 
  Tag, 
  Share2,
  Bookmark,
  Clock
} from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../hooks/useNotifications';
import { useSiteTranslation } from '../hooks/useSiteTranslation';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import {
  getPostTitle,
  getPostExcerpt,
  getPostContent,
  getBlogCategoryLabelKey
} from '../i18n/localizeContent';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import { Button } from '../components/UI';

interface Post {
  _id: string;
  title: string;
  content: string;
  excerpt: string;
  i18n?: Record<string, { title?: string; excerpt?: string; content?: string }>;
  slug: string;
  author: {
    _id: string;
    name: string;
  };
  category: string;
  tags: string[];
  featuredImage: string;
  isPublished: boolean;
  isFeatured: boolean;
  views: number;
  likes: number;
  publishedAt: string;
  createdAt: string;
}

const PostView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useSiteTranslation();
  const lang = i18n.language;
  const notifications = useNotifications();
  const { copy } = useCopyToClipboard();
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  // Só busca uma vez por id; não incluir notifications para evitar loop (objeto novo a cada render)
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    setLoading(true);
    axios
      .get(`/posts/${id}`, { signal: controller.signal, timeout: 15000 })
      .then((response) => {
        setPost(response.data);
      })
      .catch((error: unknown) => {
        if (axios.isCancel(error)) return;
        console.error('Erro ao carregar post:', error);
        notificationsRef.current.error.network();
        navigate('/blog');
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoading(false);
      });

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [id, navigate]);

  const handleLike = async () => {
    if (!post) return;
    
    try {
      // Aqui você implementaria a lógica de like
      setLiked(!liked);
      notifications.success.postUpdated();
    } catch (error: unknown) {
      console.error('Erro ao curtir post:', error);
      notifications.error.postUpdate('Erro ao curtir post');
    }
  };

  const handleBookmark = () => {
    setBookmarked(!bookmarked);
    notifications.success.postUpdated();
  };

  const handleShare = async () => {
    if (!post) return;
    const shareTitle = getPostTitle(post, lang);
    const shareExcerpt = getPostExcerpt(post, lang);
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareExcerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Erro ao compartilhar:', error);
      }
    } else {
      // Fallback para copiar link
      copy(window.location.href);
    }
  };

  const formatDate = (dateString: string) => {
    const locale = lang.startsWith('es') ? 'es-ES' : lang.startsWith('en') ? 'en-US' : 'pt-BR';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'imigração': 'bg-blue-100 text-blue-800',
      'documentos': 'bg-green-100 text-green-800',
      'vistos': 'bg-purple-100 text-purple-800',
      'cidadania': 'bg-yellow-100 text-yellow-800',
      'trabalho': 'bg-red-100 text-red-800',
      'estudos': 'bg-indigo-100 text-indigo-800',
      'geral': 'bg-gray-100 text-gray-800',
    };
    return colors[category] || colors['geral'];
  };

  const calculateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brazil-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('postView.loading')}</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('postView.notFound')}</h1>
          <p className="text-gray-600 mb-6">{t('postView.notFoundDesc')}</p>
          <Button size="sm" 
            to="/blog">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('postView.backToBlog')}
          </Button>
        </div>
      </div>
    );
  }

  const localizedTitle = getPostTitle(post, lang);
  const localizedExcerpt = getPostExcerpt(post, lang);
  const localizedContent = getPostContent(post, lang);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link 
              to="/blog" 
              className="inline-flex items-center text-gray-600 hover:text-brazil-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('postView.backToBlog')}
            </Link>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={handleLike}
                className={`inline-flex items-center px-3 py-2 rounded-lg transition-colors ${
                  liked 
                    ? 'bg-brazil-100 text-brazil-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-brazil-100 hover:text-brazil-600'
                }`}
              >
                <Heart className={`w-4 h-4 mr-2 ${liked ? 'fill-current' : ''}`} />
                {post.likes + (liked ? 1 : 0)}
              </button>
              
              <button
                onClick={handleBookmark}
                className={`inline-flex items-center px-3 py-2 rounded-lg transition-colors ${
                  bookmarked 
                    ? 'bg-brazil-100 text-brazil-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-brazil-100 hover:text-brazil-600'
                }`}
              >
                <Bookmark className={`w-4 h-4 mr-2 ${bookmarked ? 'fill-current' : ''}`} />
                {t('postView.save')}
              </button>
              
              <button
                onClick={handleShare}
                className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-brazil-100 hover:text-brazil-600 transition-colors"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {t('postView.share')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Post Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <article className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Featured Image */}
          {post.featuredImage && (
            <div className="aspect-video bg-gray-200">
              <img 
                src={post.featuredImage} 
                alt={localizedTitle}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8">
            {/* Category */}
            <div className="mb-4">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(post.category)}`}>
                {t(getBlogCategoryLabelKey(post.category))}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {localizedTitle}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-gray-600">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                <span>{post.author?.name || t('blog.authorUndefined')}</span>
              </div>
              
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{formatDate(post.publishedAt || post.createdAt)}</span>
              </div>
              
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                <span>{t('postView.readTime', { minutes: calculateReadTime(localizedContent) })}</span>
              </div>
              
              <div className="flex items-center">
                <Eye className="w-4 h-4 mr-2" />
                <span>{t('postView.views', { count: post.views })}</span>
              </div>
            </div>

            {/* Excerpt */}
            {post.excerpt && (
              <div className="mb-8">
                <p className="text-xl text-gray-700 leading-relaxed">
                  {localizedExcerpt}
                </p>
              </div>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div 
                className="text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(localizedContent).replace(/\n/g, '<br />') }}
              />
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-center mb-4">
                  <Tag className="w-4 h-4 mr-2 text-gray-600" />
                  <span className="text-sm font-medium text-gray-600">{t('postView.tags')}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-brazil-100 hover:text-brazil-700 transition-colors"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Author Info */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-brazil-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-brazil-600" />
                </div>
                <div className="ml-4">
                  <h3 className="font-semibold text-gray-900">{post.author?.name || t('blog.authorUndefined')}</h3>
                  <p className="text-gray-600">{t('postView.postAuthor')}</p>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default PostView;
